import React, { useEffect, useState } from 'react';
import { Status } from '../../../../interfaces/status';
import { ajaxFetcherGet } from '../../../../fetchers';
import Label from './Label';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, Chip, FormControlLabel, MenuItem, Select, ThemeProvider } from '@mui/material';
import { backYouTheme } from '../../../../theme';

interface Props<T>{
    selected?: string[];
    onChange?: (value: SelectOption[], slug?: string) => void;
    multiple?: boolean
    type?: string
    label?: string
    route?: string
    filter_id?: string // très mal nommé, slug d'identification du filtre
}

interface Checkbox{
    label?: string
    value?: string
    checked?: boolean
    indeterminate?: boolean
    parent?: string
    isGroup?: boolean
}

const SelectStatus = <T,>({
    selected = [],
    onChange = null,
    multiple = false,
    type = null,
    label = null,
    route = null,
    filter_id = null
}: Props<T>) => {
    
    const { t: gctrad } = useTranslation();
    const [options, setOptions] = useState<Status[]>([]);
    const [checked, setChecked] = useState<string[]>(selected);
    const [indeterminate, setIndeterminate] = useState<string[]>([]);
    const [checkboxes, setCheckboxes] = useState<Checkbox[]>([]);

    const handleCheckboxChange = (slug: string) => {

        const checkbox = checkboxes.filter((v) => v.value == slug)[0];

        // Etat de la checkbox
        checkbox.checked = !checkbox.checked;
        checkbox.indeterminate = false;

        const parent_checkbox = checkboxes.filter((v) => v.value == checkbox.parent)[0] ?? null;
        const children = checkboxes.filter((v) => v.parent == checkbox.value) ?? [];
        const siblings = checkboxes.filter((v) => v.parent == checkbox.parent) ?? [];

        // Etat du parent si on est dans un groupe d'options
        if ( parent_checkbox ) {

            // On compte le nombre d'options du même niveau qui sont cochées
            const count_siblings_checked = siblings.filter((v) => v.checked).length;

            // Nombre d'options cochées == nombre d'options du niveau => parent coché
            if ( count_siblings_checked == siblings.length ) {
                parent_checkbox.checked = true;
                parent_checkbox.indeterminate = false;
            }
            // Nombre d'options cochées > 0 => parent "indéterminé"
            else if ( count_siblings_checked > 0 ) {
                parent_checkbox.checked = false;
                parent_checkbox.indeterminate = true;
            }
            // Aucune option cochée => parent décoché
            else {
                parent_checkbox.indeterminate = false;
                parent_checkbox.checked = false;
            }
        }

        // Etat des enfants si on change un groupe
        if ( children.length ) {
            for ( const iChild in children ) {
                children[iChild].checked = checkbox.checked;
            }
        }

        const tmp_indeterminate: React.SetStateAction<string[]> = []; // état indeterminate : état d'un parent dont certains enfants sont cochés mais pas tous
        const tmp_checked: React.SetStateAction<string[]>  = [];
        const not_group_selected: SelectOption[] = [];

        checkboxes.forEach((item: Checkbox) => {
            if ( item.checked ) {
                tmp_checked.push(item.value);
                if ( !item.isGroup ) {
                    not_group_selected.push({
                        label: item.label,
                        value: item.value
                    });
                }
            }
            if ( item.indeterminate ) {
                tmp_indeterminate.push(item.value);
            }
        });

        setIndeterminate(tmp_indeterminate);
        setChecked(tmp_checked);

        // Déclenchement du onChange
        if ( filter_id ) {
            onChange(not_group_selected, filter_id)
        }

    }

    const renderValue = (selected: string[]) => {
        const not_group_selected: Checkbox[] = [];

        checkboxes.forEach((item: Checkbox) => {
            if ( selected.includes(item.value) && !item.isGroup ) {
                not_group_selected.push(item);
            }
        });

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, paddingRight: '45px'}}>
                {not_group_selected.map((item) => (
                <Chip 
                    key={"chip-" + item.value} 
                    label={item.label} 
                    size="small" 
                    onDelete={() => handleCheckboxChange(item.value)} 
                    onMouseDown={(event) => {
                        event.stopPropagation();
                    }}
                />
                ))}
            </Box>
        )
    }

    // Chargement initial des statuts
    useEffect(() => {

        ajaxFetcherGet({
            url: ENV.base_lang_url + 'myspace/providers//ajaxGetter/' + route,
        }).then((response: Status[]) => {
            const tmp_checkboxes = [];
            const parents_to_set: string[] = [];
            const tmp_indeterminate: React.SetStateAction<string[]> = []; // état indeterminate : état d'un parent dont certains enfants sont cochés mais pas tous
            const tmp_checked: React.SetStateAction<string[]>  = checked;
            for ( const i in response ) {
                tmp_checkboxes.push({
                    label: gctrad(response[i].key_trad),
                    value: response[i].slug,
                    checked: checked.includes(response[i].slug),
                    indeterminate: false,
                    parent: response[i].parent_slug ?? null,
                    isGroup: !!response[i].status_group
                })
                if ( checked.includes(response[i].slug) && (response[i].parent_slug ?? null) && !parents_to_set.includes(response[i].parent_slug) ) {
                    parents_to_set.push(response[i].parent_slug);
                }
            }
            for ( const j in parents_to_set ) {
                const parent_checkbox = tmp_checkboxes.filter((v) => v.value == parents_to_set[j])[0];
                const children = tmp_checkboxes.filter((v) => v.parent == parents_to_set[j]) ?? [];
                const count_siblings_checked = children.filter((v) => v.checked).length;
                // Nombre d'options cochées == nombre d'options du niveau => parent coché
                if ( count_siblings_checked == children.length ) {
                    parent_checkbox.checked = true;
                    parent_checkbox.indeterminate = false;
                    tmp_checked.push(parent_checkbox.value);
                }
                // Nombre d'options cochées > 0 => parent "indéterminé"
                else if ( count_siblings_checked > 0 ) {
                    parent_checkbox.checked = false;
                    parent_checkbox.indeterminate = true;
                    tmp_indeterminate.push(parent_checkbox.value);
                }
            }
            setIndeterminate(tmp_indeterminate);
            setChecked(tmp_checked);
            setCheckboxes(tmp_checkboxes);
            setOptions(response);
        })
        
    }, [route]);

	return (
        <ThemeProvider theme={backYouTheme}>
            {label && <Label label={gctrad(label)}/>}
            <Select
                multiple={multiple}
                value={checked}
                className="d-block w-100 bg-white"
                size="small"
                renderValue={(selected: string[]) => renderValue(selected)}
            >
                {options.map((option, index) => (
                    <MenuItem key={type + '-' + index + '-' + option.slug} value={option.slug} className={"p-0" + (option.parent_slug ? ' pl-4' : '')}>
                        <FormControlLabel
                            label={gctrad(option.key_trad)}
                            className="d-block p-0"
                            control={
                                <Checkbox 
                                    id={option.slug}
                                    onChange={() => handleCheckboxChange(option.slug)} 
                                    value={option.slug} 
                                    checked={checked.includes(option.slug)} 
                                    indeterminate={indeterminate.includes(option.slug)}
                                    className="py-1"
                                />
                            }
                        />
                    </MenuItem>
                ))}
            </Select>
        </ThemeProvider>
	);
};

export default SelectStatus;